import { Tooltip } from "@/components/ui/tooltip";
import { cva, type VariantProps } from "class-variance-authority";
import { InfoIcon } from "lucide-react";
import React from "react";
import {
  type Control,
  Controller,
  type ControllerProps,
  type ControllerRenderProps,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

import { cn } from "@/lib/utils/cn";
import { Label } from "./label";

const fieldVariants = cva(
  "group/field flex w-full gap-1.5 data-[invalid=true]:text-destructive",
  {
    variants: {
      orientation: {
        vertical: ["flex-col [&>*]:w-full [&>.sr-only]:w-auto"],
        horizontal: [
          "flex-row items-center",
          "[&>[data-slot=field-label]]:flex-auto",
          "has-[>[data-slot=field-content]]:items-start has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px",
        ],
        responsive: [
          "flex-col @md/field-group:flex-row @md/field-group:items-center [&>*]:w-full @md/field-group:[&>*]:w-auto [&>.sr-only]:w-auto",
          "@md/field-group:[&>[data-slot=field-label]]:flex-auto",
          "@md/field-group:has-[>[data-slot=field-content]]:items-start @md/field-group:has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px",
        ],
      },
    },
    defaultVariants: {
      orientation: "vertical",
    },
  },
);

function Field({
  className,
  orientation = "vertical",
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof fieldVariants>) {
  return (
    <div
      role="group"
      data-slot="field"
      data-orientation={orientation}
      className={cn(fieldVariants({ orientation }), className)}
      {...props}
    />
  );
}

function FieldLabel({
  className,
  tooltip,
  required,
  children,
  ...props
}: React.ComponentProps<typeof Label> & { tooltip?: string; }) {
  return (
    <Label
      data-slot="field-label"
      required={false}
      className={cn(
        "group/field-label peer/field-label flex w-fit gap-1 leading-snug group-data-[disabled=true]/field:opacity-50",
        "has-[>[data-slot=field]]:w-full has-[>[data-slot=field]]:flex-col has-[>[data-slot=field]]:rounded-md has-[>[data-slot=field]]:border [&>*]:data-[slot=field]:p-4",
        "has-data-[checked]:border-primary has-data-[checked]:bg-primary/5 dark:has-data-[checked]:bg-primary/10",
        className,
      )}
      {...props}
    >
      {children}
      {tooltip && <FieldLabelInfoTip text={tooltip} />}
      {required && <span className="text-red-500">•</span>}
    </Label>
  );
}

function FieldDescription({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      data-slot="field-description"
      className={cn(
        "text-sm leading-normal font-normal text-muted-foreground group-has-[[data-orientation=horizontal]]/field:text-balance",
        "last:mt-0 nth-last-2:-mt-1 [[data-variant=legend]+&]:-mt-1.5",
        "[&>a]:underline [&>a]:underline-offset-4 [&>a:hover]:text-primary",
        className,
      )}
      {...props}
    />
  );
}

function FieldError({
  className,
  children,
  errors,
  ...props
}: React.ComponentProps<"div"> & {
  errors?: Array<{ message?: string; } | undefined>;
}) {
  const content = React.useMemo(() => {
    if (children) {
      return children;
    }

    if (!errors?.length) {
      return null;
    }

    const uniqueErrors = [
      ...new Map(errors.map((error) => [error?.message, error])).values(),
    ];

    if (uniqueErrors?.length == 1) {
      return uniqueErrors[0]?.message;
    }

    return (
      <ul className="ml-4 flex list-disc flex-col gap-1">
        {uniqueErrors.map(
          (error, index) => error?.message && <li key={index}>{error.message}</li>,
        )}
      </ul>
    );
  }, [children, errors]);

  if (!content) {
    return null;
  }

  return (
    <div
      role="alert"
      data-slot="field-error"
      className={cn("text-sm font-normal text-destructive", className)}
      {...props}
    >
      {content}
    </div>
  );
}

type ControllerFormFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = {
  control: Control<TFieldValues>;
  name: TName;
  label?: string;
  required?: boolean;
  description?: string;
  className?: string;
  /** Additional react-hook-form validation rules (validate, pattern, min, etc).
   *  ``required`` is auto-merged from the ``required`` prop unless overridden here. */
  rules?: ControllerProps<TFieldValues, TName>["rules"];
  children: (
    field: ControllerRenderProps<TFieldValues, TName> & { id: string; },
  ) => React.ReactNode;
};

function ControllerFormField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  required,
  description,
  className,
  rules,
  children,
}: ControllerFormFieldProps<TFieldValues, TName>) {
  const mergedRules = {
    ...(required ? { required: `${label ?? name} is required` } : {}),
    ...rules,
  };
  return (
    <Controller
      control={control}
      name={name}
      rules={mergedRules}
      render={({ field, fieldState }) => (
        <Field data-invalid={fieldState.invalid} className={className}>
          {label && (
            <FieldLabel htmlFor={name} required={!!required}>
              {label}
            </FieldLabel>
          )}
          {children({ ...field, id: name })}
          {description && <FieldDescription>{description}</FieldDescription>}
          {fieldState.error && <FieldError errors={[fieldState.error]} />}
        </Field>
      )}
    />
  );
}

function FieldLabelInfoTip({ text }: { text: React.ReactNode; }) {
  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <InfoIcon className="size-3 text-muted-foreground shrink-0 self-center cursor-help" />
        }
      />
      <Tooltip.Content className="max-w-xs text-xs font-normal">{text}</Tooltip.Content>
    </Tooltip>
  );
}

export { ControllerFormField, Field, FieldDescription, FieldError, FieldLabel, FieldLabelInfoTip };
